import { CONTROL_LIT } from "@majourneys/shared/ui";
import { useTranslation } from "react-i18next";
import type { RelatedLink } from "../seo/category-seo";

import { Icon } from "@majourneys/icons";

/**
 * One-line cross-links. These read as chips, not as cards, so they wear the
 * plain white control edge — the pewter band is for surfaces you read.
 */
const NAVY = "#110E39";
const MUTED = "#5a5d77";
/** A control at rest — the one definition, in `@majourneys/shared/ui`. */
const CONTROL_FILL = CONTROL_LIT;
const CONTROL_EDGE = "rgba(17,14,57,0.16)";

interface CategoryRelatedProps {
  related: RelatedLink[];
  cityName: string | null;
  categoryName: string | null;
}

export function CategoryRelated({ related, cityName, categoryName }: CategoryRelatedProps) {
  const { t } = useTranslation();
  if (related.length === 0) return null;

  const heading = cityName && categoryName
    ? t("activity.also_popular_in", { defaultValue: "Also popular in {{city}}", city: cityName })
    : categoryName
      ? t("activity.category_by_city", { defaultValue: "{{category}} by city", category: categoryName })
      : t("activity.you_might_also_like", { defaultValue: "You might also like" });

  return (
    <section aria-label={t("activity.related_collections")} className="py-10">
      <div className="mb-5 flex items-end justify-between gap-3">
        <h2
          className="text-[22px] font-semibold leading-tight sm:text-[26px]"
          style={{ color: NAVY, fontFamily: "var(--font-geist)", letterSpacing: "-0.025em" }}
        >
          {heading}
        </h2>
      </div>
      <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {related.map((r) => (
          <li key={r.href}>
            <a
              href={r.href}
              className="group flex items-center justify-between gap-3 rounded-2xl px-4 py-3 text-[14px] font-semibold transition hover:-translate-y-[1px] hover:text-[color:var(--color-primary)]"
              style={{ color: NAVY, background: CONTROL_FILL, boxShadow: `0 0 0 1.5px ${CONTROL_EDGE}` }}
            >
              <span className="flex items-center gap-2">
                <Icon name={r.kind === "city"
                      ? "ri-map-pin-line"
                      : r.kind === "category"
                        ? "ri-compass-3-line"
                        : "ri-links-line"} className={`text-[16px] text-[color:var(--color-primary)]`} aria-hidden="true" />
                {r.label}
              </span>
              <Icon name="ri-arrow-right-up-line" className="text-[16px] text-[#5a5d77] transition group-hover:translate-x-[1px] group-hover:text-[color:var(--color-primary)]" aria-hidden="true" />
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
